import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Users,
  DollarSign,
  Plus,
  Settings,
  RefreshCw,
  Loader2,
  ArrowLeft,
  TrendingUp,
  Package,
  Wallet,
  Eye,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  LogIn
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminSellersPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sellers, setSellers] = useState([]);
  const [globalStats, setGlobalStats] = useState({});
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showPayoutDialog, setShowPayoutDialog] = useState(false);
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [creating, setCreating] = useState(false);

  const [newSeller, setNewSeller] = useState({
    email: '',
    password: '',
    company_name: '',
    contact_name: '',
    phone: ''
  });

  const [paymentRates, setPaymentRates] = useState({
    cold_call: 0,
    live_transfer: 0,
    data_lead: 0
  });

  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutNotes, setPayoutNotes] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('dme_token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch sellers
      const sellersRes = await axios.get(`${API_URL}/api/admin/sellers`, { headers });
      setSellers(sellersRes.data || []);

      // Fetch global stats
      const statsRes = await axios.get(`${API_URL}/api/admin/seller-stats`, { headers });
      setGlobalStats(statsRes.data || {});

    } catch (error) {
      toast.error('Failed to load sellers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateSeller = async () => {
    try {
      setCreating(true);
      const token = localStorage.getItem('dme_token');
      await axios.post(
        `${API_URL}/api/admin/sellers`,
        newSeller,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Seller created successfully');
      setShowCreateDialog(false);
      setNewSeller({ email: '', password: '', company_name: '', contact_name: '', phone: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create seller');
    } finally {
      setCreating(false);
    }
  };

  const handleUpdatePaymentRates = async () => {
    try {
      const token = localStorage.getItem('dme_token');
      await axios.put(
        `${API_URL}/api/admin/sellers/${selectedSeller.id}/payment-rates`,
        paymentRates,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Payment rates updated');
      setShowEditDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update payment rates');
    }
  };

  const handleToggleActive = async (sellerId, currentStatus) => {
    try {
      const token = localStorage.getItem('dme_token');
      await axios.put(
        `${API_URL}/api/admin/sellers/${sellerId}`,
        { is_active: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSellers(prev => prev.map(s => 
        s.id === sellerId ? { ...s, is_active: !currentStatus } : s
      ));
      toast.success(`Seller ${!currentStatus ? 'activated' : 'deactivated'}`);
    } catch (error) {
      toast.error('Failed to update seller status');
    }
  };

  const handleProcessPayout = async () => {
    const amount = parseFloat(payoutAmount);
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (amount > selectedSeller.balance) {
      toast.error('Amount exceeds available balance');
      return;
    }

    try {
      const token = localStorage.getItem('dme_token');
      await axios.post(
        `${API_URL}/api/admin/sellers/${selectedSeller.id}/payout`,
        { amount, notes: payoutNotes, method: 'manual' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Payout processed successfully');
      setShowPayoutDialog(false);
      setPayoutAmount('');
      setPayoutNotes('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process payout');
    }
  };

  const openEditDialog = (seller) => {
    setSelectedSeller(seller);
    setPaymentRates({
      cold_call: seller.payment_rates?.cold_call || 0,
      live_transfer: seller.payment_rates?.live_transfer || 0,
      data_lead: seller.payment_rates?.data_lead || 0
    });
    setShowEditDialog(true);
  };

  const openPayoutDialog = (seller) => {
    setSelectedSeller(seller);
    setPayoutAmount('');
    setPayoutNotes('');
    setShowPayoutDialog(true);
  };

  const handleImpersonate = async (seller) => {
    if (!window.confirm(`Impersonate "${seller.company_name}"? You will be logged in as this seller.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('dme_token');
      const response = await axios.post(
        `${API_URL}/api/admin/sellers/${seller.id}/impersonate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Store the impersonation token and seller data
      localStorage.setItem('seller_token', response.data.access_token);
      localStorage.setItem('seller_data', JSON.stringify(response.data.seller));
      localStorage.setItem('impersonating_seller', 'true');
      localStorage.setItem('admin_return_token', token);
      
      toast.success(`Now viewing as ${seller.company_name}`);
      
      // Navigate to seller dashboard
      navigate('/seller/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to impersonate seller');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-green-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="admin-sellers-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Users className="w-6 h-6 text-green-600" />
              Seller Management
            </h1>
            <p className="text-muted-foreground">Manage lead sellers and payment settings</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={fetchData} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="bg-green-600 hover:bg-green-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Seller
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Seller</DialogTitle>
                <DialogDescription>Add a new seller account to the platform</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Company Name *</Label>
                  <Input
                    value={newSeller.company_name}
                    onChange={(e) => setNewSeller(prev => ({ ...prev, company_name: e.target.value }))}
                    placeholder="Acme Lead Co"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={newSeller.email}
                    onChange={(e) => setNewSeller(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="seller@example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Password *</Label>
                  <Input
                    type="password"
                    value={newSeller.password}
                    onChange={(e) => setNewSeller(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="••••••••"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Contact Name</Label>
                    <Input
                      value={newSeller.contact_name}
                      onChange={(e) => setNewSeller(prev => ({ ...prev, contact_name: e.target.value }))}
                      placeholder="John Smith"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input
                      value={newSeller.phone}
                      onChange={(e) => setNewSeller(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="555-123-4567"
                    />
                  </div>
                </div>
                <Button 
                  onClick={handleCreateSeller} 
                  className="w-full bg-green-600 hover:bg-green-700"
                  disabled={creating || !newSeller.email || !newSeller.password || !newSeller.company_name}
                >
                  {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                  Create Seller
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Global Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{globalStats.total_sellers || 0}</p>
                <p className="text-sm text-muted-foreground">Total Sellers</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{globalStats.active_sellers || 0}</p>
                <p className="text-sm text-muted-foreground">Active</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Package className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{globalStats.total_leads_submitted || 0}</p>
                <p className="text-sm text-muted-foreground">Total Leads</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Wallet className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{formatCurrency(globalStats.total_pending_balance)}</p>
                <p className="text-sm text-muted-foreground">Pending Payouts</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-teal-100 rounded-lg">
                <TrendingUp className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{formatCurrency(globalStats.total_paid_out_all_time)}</p>
                <p className="text-sm text-muted-foreground">Total Paid Out</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sellers Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Sellers</CardTitle>
          <CardDescription>Manage seller accounts and payment settings</CardDescription>
        </CardHeader>
        <CardContent>
          {sellers.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No sellers yet</p>
              <p className="text-sm">Create your first seller to get started</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Leads</TableHead>
                  <TableHead>Sold</TableHead>
                  <TableHead>Balance</TableHead>
                  <TableHead>Rates (CC/LT/DL)</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Login</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sellers.map((seller) => (
                  <TableRow key={seller.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{seller.company_name}</p>
                        <p className="text-xs text-muted-foreground">{seller.email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="text-sm">{seller.contact_name || '-'}</p>
                        <p className="text-xs text-muted-foreground">{seller.phone || '-'}</p>
                      </div>
                    </TableCell>
                    <TableCell>{seller.total_leads_submitted || 0}</TableCell>
                    <TableCell>
                      <span className="text-green-600 font-medium">{seller.total_leads_sold || 0}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-green-600 font-bold">
                        {formatCurrency(seller.balance)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                        ${seller.payment_rates?.cold_call || 0} / 
                        ${seller.payment_rates?.live_transfer || 0} / 
                        ${seller.payment_rates?.data_lead || 0}
                      </code>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={seller.is_active}
                          onCheckedChange={() => handleToggleActive(seller.id, seller.is_active)}
                        />
                        <Badge variant={seller.is_active ? 'default' : 'secondary'}>
                          {seller.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(seller.last_login_at)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleImpersonate(seller)}
                          disabled={!seller.is_active}
                          title="Impersonate seller"
                          className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                        >
                          <LogIn className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEditDialog(seller)}
                          title="Edit payment rates"
                        >
                          <Settings className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openPayoutDialog(seller)}
                          disabled={!seller.balance || seller.balance <= 0}
                          title="Process payout"
                          className="text-green-600"
                        >
                          <DollarSign className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => navigate(`/admin/sellers/${seller.id}`)}
                          title="View details"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit Payment Rates Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Payment Rates - {selectedSeller?.company_name}</DialogTitle>
            <DialogDescription>Set payment rates per lead type for this seller</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Cold Call Rate ($)</Label>
              <Input
                type="number"
                step="0.01"
                value={paymentRates.cold_call}
                onChange={(e) => setPaymentRates(prev => ({ ...prev, cold_call: parseFloat(e.target.value) || 0 }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Live Transfer Rate ($)</Label>
              <Input
                type="number"
                step="0.01"
                value={paymentRates.live_transfer}
                onChange={(e) => setPaymentRates(prev => ({ ...prev, live_transfer: parseFloat(e.target.value) || 0 }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Data Lead Rate ($)</Label>
              <Input
                type="number"
                step="0.01"
                value={paymentRates.data_lead}
                onChange={(e) => setPaymentRates(prev => ({ ...prev, data_lead: parseFloat(e.target.value) || 0 }))}
              />
            </div>
            <Button onClick={handleUpdatePaymentRates} className="w-full bg-green-600 hover:bg-green-700">
              Save Payment Rates
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Process Payout Dialog */}
      <Dialog open={showPayoutDialog} onOpenChange={setShowPayoutDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Process Payout - {selectedSeller?.company_name}</DialogTitle>
            <DialogDescription>
              Available balance: <span className="text-green-600 font-bold">{formatCurrency(selectedSeller?.balance)}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Payout Amount ($)</Label>
              <Input
                type="number"
                step="0.01"
                value={payoutAmount}
                onChange={(e) => setPayoutAmount(e.target.value)}
                placeholder="0.00"
                max={selectedSeller?.balance || 0}
              />
            </div>
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Input
                value={payoutNotes}
                onChange={(e) => setPayoutNotes(e.target.value)}
                placeholder="Payment reference, check number, etc."
              />
            </div>
            <Button onClick={handleProcessPayout} className="w-full bg-green-600 hover:bg-green-700">
              <DollarSign className="w-4 h-4 mr-2" />
              Process Payout
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
